import { useState } from "react";
import { Check } from "lucide-react";
import type { MCQConfig, Question, Response } from "../../lib/types";
import { submitResponse } from "../../lib/api";

type Props = {
  question: Question;
  streamId?: string | null;
  onSubmitted: (row: Response) => void;
  onError: (message: string) => void;
};

const OTHER_KEY = "__other__";

export function MCQAnswer({ question, streamId, onSubmitted, onError }: Props) {
  const cfg = question.config as MCQConfig;
  const multi = cfg.multi_select === true;
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [otherText, setOtherText] = useState("");
  const [why, setWhy] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isOtherSelected = selectedKeys.includes(OTHER_KEY);
  const otherRequired = isOtherSelected && cfg.why_required_for_other;
  const otherValid = !otherRequired || otherText.trim().length > 0;
  const canSubmit = selectedKeys.length > 0 && !submitting && otherValid;

  function toggle(key: string) {
    setSelectedKeys((prev) => {
      if (multi) {
        return prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      }
      return [key];
    });
  }

  async function handleSubmit() {
    if (selectedKeys.length === 0) return;
    setSubmitting(true);
    try {
      let row: Response;
      if (multi) {
        row = await submitResponse({
          questionId: question.id,
          text: isOtherSelected ? otherText.trim() : "",
          choiceKeys: selectedKeys,
          streamId,
        });
      } else {
        const selectedKey = selectedKeys[0];
        const text = isOtherSelected
          ? otherText.trim()
          : cfg.why_for_all ? why.trim() : "";
        row = await submitResponse({
          questionId: question.id,
          text,
          choiceKey: selectedKey,
          isOther: isOtherSelected,
          streamId,
        });
      }
      onSubmitted(row);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Senden fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  }

  function optionButton(key: string, label: string) {
    const selected = selectedKeys.includes(key);
    return (
      <button
        type="button"
        onClick={() => toggle(key)}
        role={multi ? "checkbox" : "radio"}
        aria-checked={selected}
        className={[
          "w-full rounded-xl border px-4 py-3 text-left text-rc-ink transition flex items-center gap-3",
          selected
            ? "border-rc-berry bg-rc-berry/5 shadow-berry"
            : "border-rc-ink/10 bg-rc-paper hover:border-rc-ink/30",
        ].join(" ")}
      >
        <span
          aria-hidden="true"
          className={[
            "flex h-5 w-5 flex-none items-center justify-center border transition",
            multi ? "rounded-md" : "rounded-full",
            selected ? "border-rc-berry bg-rc-berry" : "border-rc-ink/25 bg-rc-paper",
          ].join(" ")}
        >
          {selected && (multi
            ? <Check className="h-3.5 w-3.5 text-rc-cream" strokeWidth={3} />
            : <span className="h-2 w-2 rounded-full bg-rc-cream" />)}
        </span>
        {label}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ul
        className="flex flex-col gap-2"
        role={multi ? "group" : "radiogroup"}
        aria-label={question.text}
      >
        {cfg.options.map((opt) => (
          <li key={opt.key}>{optionButton(opt.key, opt.label)}</li>
        ))}
        {cfg.allow_other && (
          <li>
            {optionButton(OTHER_KEY, cfg.other_label)}
            {isOtherSelected && (
              <textarea
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                rows={3}
                className="mt-2 w-full rounded-lg border border-rc-ink/10 bg-rc-paper p-3 text-rc-ink focus:outline-none focus:ring-2 focus:ring-rc-berry"
                placeholder="Bitte beschreiben Sie Ihr Thema."
              />
            )}
          </li>
        )}
      </ul>

      {multi && (
        <p className="text-sm text-rc-ink/60 -mt-2">Mehrfachauswahl möglich.</p>
      )}

      {!multi && cfg.why_for_all && selectedKeys.length > 0 && !isOtherSelected && (
        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-rc-ink">{cfg.why_prompt}</span>
          <textarea
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            rows={3}
            className="rounded-lg border border-rc-ink/10 bg-rc-paper p-3 text-rc-ink focus:outline-none focus:ring-2 focus:ring-rc-berry"
          />
        </label>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="rounded-xl bg-rc-berry px-6 py-3 text-rc-cream font-medium shadow-berry transition disabled:opacity-50 disabled:shadow-none"
      >
        {submitting ? "Wird gesendet…" : "Antwort senden"}
      </button>
    </div>
  );
}
